import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, FlatList, ActivityIndicator, ScrollView } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSource } from '../services/ListsManager';
import ListsManager from '../services/ListsManager';
import TweetActionsManager from '../services/TweetActionsManager';
import { useTheme } from '../theme/ThemeContext';

// ReactionModal removed - lifted state up
// Import social media icons
import TwitterIcon from '../assets/images/twitter-icon.png';
import TelegramIcon from '../assets/images/telegram-icon.png';
import InstagramIcon from '../assets/images/instagram-icon.png';
// Import react-icons
import { FaReply, FaQuoteRight, FaInfoCircle, FaExternalLinkAlt, FaShareAlt, FaLanguage, FaRetweet, FaTimes, FaSearch, FaTag, FaUndo, FaPlay, FaMagic, FaTiktok, FaTrash, FaBinoculars, FaFacebookF, FaTv } from 'react-icons/fa';
import ShareModal from './ShareModal';
import { useOrg } from '../hooks/useOrg';

const TweetCell = ({
  tweet,
  listId, // Added prop
  hideActions = false,
  isSelectionModeEnabled = false,
  isSelected = false,
  currentUserLanguage = null,
  onSelect = null,
  onReaction = null, // Callback for reaction (lifted state)
  onRemove = null, // Callback after message is successfully removed
  sortBy = 'inserted_at', // 'created_at' or 'inserted_at'
  highlightText = '', // Search text to highlight in content
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const navigate = useNavigate();
  const [isTracking, setIsTracking] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const [isRetweet, setIsRetweet] = useState(false);
  const [retweetInfo, setRetweetInfo] = useState(null);
  const [formattedContent, setFormattedContent] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const [userLanguage, setUserLanguage] = useState(currentUserLanguage || 'he');

  // Reaction Modal State removed (lifted state up)

  const isFromTelegram = tweet.source === MessageSource.TELEGRAM;
  const isFromTiktok = tweet.source === MessageSource.TIKTOK;
  const isFromInstagram = tweet.source === MessageSource.INSTAGRAM;
  const isFromRss = tweet.source === 'rss';
  const isFromWebz = tweet.source === 'webz';
  const isFromTv = tweet.source === 'tv';
  const isFromFacebookGroup = tweet.source === MessageSource.FACEBOOK_GROUP;

  const tiktokVideoId = isFromTiktok && tweet.url
    ? (tweet.url.match(/video\/(\d+)/) || [])[1] || null
    : null;

  const getRssSiteUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const parts = parsed.hostname.split('.');
      const last = parts[parts.length - 1];
      const secondLast = parts[parts.length - 2] || '';
      const isTwoPart = last.length === 2 && secondLast.length <= 3;
      const tldCount = isTwoPart ? 2 : 1;
      const rootDomain = parts.slice(-(tldCount + 1)).join('.');
      const firstSegment = parsed.pathname.split('/').filter(Boolean)[0] || '';
      return firstSegment ? `https://${rootDomain}/${firstSegment}` : `https://${rootDomain}`;
    } catch {
      return url;
    }
  };

  const hasPreTranslated = !!tweet.translated_content;
  const [isTranslated, setIsTranslated] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);

  // Function to check tweet type
  const getTweetType = () => {
    if (!tweet.dependencies || tweet.dependencies.length === 0) {
      return null;
    }

    const dependency = tweet.dependencies[0];
    if (dependency.includes("(replied_to)")) {
      return "reply";
    } else if (dependency.includes("(quoted)") || dependency.includes("(part_of_conversation)")) {
      return "quote";
    } else if (dependency.includes("(retweeted)")) {
      return "retweet";
    }
    return null;
  };

  // Decide initial content to show.
  // Retweet detection must always be based on the original tweet_content.
  useEffect(() => {
    let isRT = false;
    let parsed = null;

    if (tweet.tweet_content && tweet.tweet_content.startsWith('RT @')) {
      parsed = parseDependencyDetails(tweet.tweet_content);
      if (parsed) {
        isRT = true;
      }
    }

    setIsRetweet(isRT);
    setRetweetInfo(parsed);

    // Decide which text to display by default
    let contentToUse;
    if (isRT) {
      contentToUse = parsed ? parsed.content : tweet.tweet_content;
    } else {
      contentToUse = tweet.tweet_content;
    }

    if (contentToUse) {
      setFormattedContent(formatRichText(contentToUse));
    }
  }, [tweet, hasPreTranslated]);

  // Helper function to parse retweet details
  const parseDependencyDetails = (content) => {
    const pattern = /RT @([\w]+):\s*(.*)/;
    const match = content.match(pattern);

    if (!match || match.length < 3) {
      return null;
    }

    return {
      username: match[1],
      content: match[2]
    };
  };

  // Handle draft reaction - delegate to parent
  const handleDraftReaction = () => {
    if (onReaction) {
      onReaction(tweet, listId);
    }
  };

  // Handle message removal (optimistic)
  const handleRemove = () => {
    if (!window.confirm(t('app.tweetCell.removeConfirm'))) return;
    if (onRemove) onRemove(tweet);
    ListsManager.removeMessage(tweet.tweet_id, tweet.source || 'twitter').catch(err =>
      console.error('Failed to remove message:', err)
    );
  };


  const handleTrackPost = async () => {
    if (isTracked) return;
    setIsTracking(true);
    try {
      const platform = isFromTelegram ? 'telegram' : isFromTiktok ? 'tiktok' : isFromInstagram ? 'instagram' : isFromFacebookGroup ? 'facebook_group' : isFromWebz ? 'webz' : isFromRss ? 'rss' : isFromTv ? 'tv' : 'twitter';
      const postId = tweet.tweet_id || tweet.id;
      const body = {
        tracker_type: 'saved_content',
        title: `${tweet.author_name || tweet.username || 'Post'}: ${(tweet.tweet_content || tweet.translated_content || '').slice(0, 60)}`,
        source_post_id: String(postId),
        source_platform: platform,
        source_url: tweet.url || null,
        schedule_pattern: 'passive',
      };
      if (activeOrg?.id) body.org_id = activeOrg.id;
      if (listId) body.list_id = listId;
      const result = await ListsManager.createTracker(body);
      if (result) setIsTracked(true);
    } catch (err) {
      console.error('Track post failed:', err);
    } finally {
      setIsTracking(false);
    }
  };

  // Format rich text similar to Swift's formatItineraryText
  const formatRichText = (text) => {
    if (!text) return null;

    const parts = [];
    let currentIndex = 0;

    // Regular expressions for bold, markdown links, and raw URLs
    // Group 1,2: Bold (**text**)
    // Group 3,4,5: Markdown Link ([text](url))
    // Group 6: Raw URL (https://...)
    const combinedRegex = /(\*\*(.*?)\*\*)|(\[(.*?)\]\((https?:\/\/[^\s]+)\))|(https?:\/\/[^\s]+)/g;
    let match;

    while ((match = combinedRegex.exec(text)) !== null) {
      // Add text before the matched part
      if (match.index > currentIndex) {
        parts.push({
          text: text.substring(currentIndex, match.index),
          isBold: false,
          isLink: false
        });
      }

      // Check if it's a bold match (group 1 and 2)
      if (match[1]) {
        parts.push({
          text: match[2], // The text inside **
          isBold: true,
          isLink: false
        });
      }
      // Check if it's a markdown link match (group 3, 4, and 5)
      else if (match[3]) {
        parts.push({
          text: match[4], // The display text
          url: match[5], // The URL
          isBold: false,
          isLink: true
        });
      }
      // Check if it's a raw URL match (group 6)
      else if (match[6]) {
        parts.push({
          text: match[6], // The URL IS the display text
          url: match[6], // The URL
          isBold: false,
          isLink: true
        });
      }

      currentIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (currentIndex < text.length) {
      parts.push({
        text: text.substring(currentIndex),
        isBold: false,
        isLink: false
      });
    }

    return parts.length > 0 ? parts : [{ text, isBold: false, isLink: false }];
  };

  const tweetType = getTweetType();

  // Get user's language preference on component mount
  useEffect(() => {
    if (currentUserLanguage) {
      setUserLanguage(currentUserLanguage);
      return;
    }

    const fetchUserLanguage = async () => {
      try {
        const language = await ListsManager.getListenerLanguage();
        setUserLanguage(language);
      } catch (error) {
        console.error('Error fetching user language:', error);
      }
    };
    fetchUserLanguage();
  }, [currentUserLanguage]);

  // Check if tweet needs translation
  const isTweetEnglish = (tweetContent) => {
    const lang = tweet?.lang || tweet?.language || tweet?.detected_language || null;
    if (lang && typeof lang === 'string') {
      return lang.toLowerCase().startsWith(userLanguage.toLowerCase());
    }
    return false; // Unknown language; assume not in user's language
  };

  // Handle translation
  const handleTranslatePress = async () => {
    if (isTranslated) {
      setIsTranslated(false);
      const contentToUse = isRetweet ? retweetInfo?.content : tweet.tweet_content;
      setFormattedContent(formatRichText(contentToUse));
      return;
    }

    // If we already fetched a translation this session, reuse it
    if (translatedContent) {
      setIsTranslated(true);
      setFormattedContent(formatRichText(translatedContent));
      return;
    }

    const contentToTranslate = isRetweet ? retweetInfo?.content : tweet.tweet_content;
    if (!contentToTranslate) return;

    if (isTweetEnglish(contentToTranslate)) {
      alert(t('app.tweetCell.alreadyInLanguage'));
      return;
    }

    setIsTranslating(true);

    try {
      const translated = await ListsManager.translateTweet(contentToTranslate, userLanguage);
      if (translated && translated !== contentToTranslate) {
        setTranslatedContent(translated);
        setIsTranslated(true);
        setFormattedContent(formatRichText(translated));
      } else {
        alert(t('app.tweetCell.translationNotAvailable'));
      }
    } catch (error) {
      console.error('Translation error:', error);
      alert(t('app.tweetCell.translationFailed'));
    } finally {
      setIsTranslating(false);
    }
  };

  // Handle various tweet interactions
  const handleReplyPress = () => {
    TweetActionsManager.handleReplyTapped(tweet, { navigate });
  };

  const handleQuotePress = () => {
    TweetActionsManager.handleQuoteTapped(tweet, { navigate });
  };

  const handleAuthorPress = () => {
    // For Telegram, use username if available, otherwise use author_name without the @ symbol
    const username = isFromTelegram
      ? (tweet.username || (tweet.author_name?.startsWith('@') ? tweet.author_name.substring(1) : tweet.author_name))
      : tweet.username || tweet.author_name;

    if (isFromTiktok) {
      const tiktokUsername = tweet.username || tweet.author_name;
      window.open(`https://www.tiktok.com/@${tiktokUsername}`, '_blank');
      return;
    }

    if (isFromInstagram) {
      window.open(tweet.url || `https://www.instagram.com/${username}/`, '_blank');
      return;
    }

    if (isFromFacebookGroup) {
      if (tweet.url) window.open(tweet.url, '_blank');
      return;
    }

    TweetActionsManager.handleAuthorNamePress(username, isFromTelegram, { navigate });
  };

  const handleSharePress = () => {
    setShowShareModal(true);
  };

  const handleOpenPress = () => {
    TweetActionsManager.handleOpenButtonTapped(tweet.url, { navigate });
  };

  const handleInfoPress = () => {
    setShowReasonModal(true);
  };

  const renderHighlightedText = (text, baseStyle) => {
    if (!highlightText || !text) return <Text style={baseStyle}>{text}</Text>;
    const escaped = highlightText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const segments = text.split(regex);
    if (segments.length === 1) return <Text style={baseStyle}>{text}</Text>;
    return segments.map((seg, i) =>
      regex.test(seg)
        ? <Text key={i} style={[baseStyle, styles.highlightedText]}>{seg}</Text>
        : <Text key={i} style={baseStyle}>{seg}</Text>
    );
  };

  // Render formatted text with translation support
  const renderFormattedText = () => {
    const translatedToShow = hasPreTranslated ? tweet.translated_content : translatedContent;
    const contentToRender = isTranslated ? translatedToShow : (isRetweet ? retweetInfo?.content : tweet.tweet_content);
    const formattedToRender = isTranslated ? formattedContent : formattedContent;

    if (!formattedToRender) {
      return <Text style={styles.tweetContent}>{renderHighlightedText(contentToRender, null)}</Text>;
    }

    return (
      <View>
        <Text style={styles.tweetContent}>
          {formattedToRender.map((part, index) => {
            if (part.isLink) {
              return (
                <Text
                  key={index}
                  style={styles.linkText}
                  onClick={() => window.open(part.url, '_blank')}
                >
                  {renderHighlightedText(part.text, styles.linkText)}
                </Text>
              );
            } else {
              return (
                <Text
                  key={index}
                  style={part.isBold ? styles.boldText : null}
                >
                  {renderHighlightedText(part.text, part.isBold ? styles.boldText : null)}
                </Text>
              );
            }
          })}
        </Text>

        {/* Translation indicator */}
        {isTranslated && (
          <View style={styles.translationIndicator}>
            <FaLanguage style={styles.translationIcon} />
            <Text style={styles.translationText}>{t('app.tweetCell.translatedFromOriginal')}</Text>
          </View>
        )}
      </View>
    );
  };

  // Render the reason modal
  const renderReasonModal = () => {
    if (!showReasonModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.overlay,
        backdropFilter: 'blur(8px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        padding: '20px',
        animation: 'fadeIn 0.2s ease-out'
      }}>
        <div style={{
          backgroundColor: theme.bg,
          backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.15), transparent 60%)',
          borderRadius: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          {/* Modal Header */}
          <div style={{
            padding: '24px',
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.bgCard,
            backdropFilter: 'blur(24px)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(77, 166, 255, 0.15)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid rgba(77, 166, 255, 0.3)'
              }}>
                <FaSearch style={{
                  color: '#4DA6FF',
                  fontSize: '18px'
                }} />
              </div>
              <Text style={{
                color: theme.textPrimary,
                fontSize: '20px',
                fontWeight: '700',
                fontFamily: 'Outfit, sans-serif',
                letterSpacing: '-0.02em',
              }}>
                {t('app.tweetCell.whyThisAppeared')}
              </Text>
            </div>
            <TouchableOpacity
              onPress={() => setShowReasonModal(false)}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '10px',
                backgroundColor: theme.bgSurface,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.2s'
              }}
            >
              <FaTimes style={{ color: theme.textSecondary, fontSize: '14px' }} />
            </TouchableOpacity>
          </div>

          {/* Modal Content */}
          <div style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1
          }}>
            {/* Reason Card */}
            <div style={{
              backgroundColor: 'rgba(77, 166, 255, 0.05)',
              borderRadius: '20px',
              padding: '24px',
              border: '1px solid rgba(77, 166, 255, 0.15)',
              marginBottom: '24px'
            }}>
              <Text style={{
                color: theme.textPrimary,
                fontSize: '16px',
                lineHeight: '26px',
                fontFamily: 'Outfit, sans-serif'
              }}>
                {tweet.reason || t('app.tweetCell.defaultReason')}
              </Text>
            </div>

            {/* Dependencies Section */}
            {tweet.dependencies && tweet.dependencies.length > 0 && (
              <div>
                <Text style={{
                  color: theme.textSecondary,
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '16px',
                  marginLeft: '4px'
                }}>
                  {t('app.tweetCell.relatedContent')}
                </Text>
                {tweet.dependencies.map((dependency, index) => (
                  <div key={index} style={{
                    backgroundColor: theme.bgSurface,
                    borderRadius: '16px',
                    padding: '16px',
                    marginBottom: '12px',
                    border: `1px solid ${theme.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <FaTag style={{ color: theme.textMuted, fontSize: '14px' }} />
                    <Text style={{
                      color: theme.textSecondary,
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontFamily: 'Outfit, sans-serif'
                    }}>
                      {dependency}
                    </Text>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Convert UTC time to local time with nice formatting
  const formatLocalTime = (utcTimeString) => {
    if (!utcTimeString) return 'Unknown';

    try {
      let utcDate;

      // Ensure we're parsing as UTC time
      if (utcTimeString.endsWith('Z') || utcTimeString.includes('+') || utcTimeString.includes('T')) {
        // Already has timezone info
        utcDate = new Date(utcTimeString);
      } else {
        // Assume it's UTC and add 'Z' to make it explicit
        utcDate = new Date(utcTimeString + 'Z');
      }

      // If that fails, try parsing as ISO string with explicit UTC
      if (isNaN(utcDate.getTime())) {
        // Try parsing as ISO string and treat as UTC
        const isoString = utcTimeString.replace(' ', 'T') + (utcTimeString.includes('Z') ? '' : 'Z');
        utcDate = new Date(isoString);
      }

      // Check if the date is valid
      if (isNaN(utcDate.getTime())) {
        console.error('Invalid date format:', utcTimeString);
        return utcTimeString; // Return original if parsing failed
      }

      // Now utcDate is in UTC, and when we display it, JavaScript automatically converts to local time
      const now = new Date();
      const diffInMs = now.getTime() - utcDate.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      // Format relative time for recent tweets
      if (diffInMinutes < 1) {
        return t('app.tweetCell.justNow');
      } else if (diffInMinutes < 60) {
        return t('app.tweetCell.minutesAgo', { n: diffInMinutes });
      } else if (diffInHours < 24) {
        return t('app.tweetCell.hoursAgo', { n: diffInHours });
      } else if (diffInDays < 7) {
        return t('app.tweetCell.daysAgo', { n: diffInDays });
      } else {
        // For older tweets, show formatted date in local timezone
        return utcDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: utcDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
          hour: '2-digit',
          minute: '2-digit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone // Explicit local timezone
        });
      }
    } catch (error) {
      console.error("Error formatting date:", error, "Original:", utcTimeString);
      return utcTimeString; // Return original if error occurs
    }
  };

  return (
    <View style={styles.container}>
      {/* Retweet Label */}
      {isRetweet && (
        <View style={styles.retweetLabelContainer}>
          <FaRetweet style={styles.retweetLabelIcon} />
          <Text style={styles.retweetLabel}>
            {t('app.tweetCell.reposted', { name: tweet.author_name })}
          </Text>
        </View>
      )}

      {/* Header with user info */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.userInfo} onPress={handleAuthorPress}>
          {tweet.author_profile_image_url ? (
            <Image
              source={{ uri: tweet.author_profile_image_url }}
              style={styles.profileImage}
            />
          ) : (isFromRss || isFromWebz) && tweet.url ? (
            <Image
              source={{ uri: (() => { try { const p = new URL(tweet.url); return `https://www.google.com/s2/favicons?domain=${p.hostname}&sz=64`; } catch { return ''; } })() }}
              style={styles.profileImage}
            />
          ) : isFromTv ? (
            <View style={[styles.avatar, { backgroundColor: '#9f1239' }]}>
              <FaTv style={{ color: '#fff', fontSize: 18 }} />
            </View>
          ) : (
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {tweet.author_name ? tweet.author_name.charAt(0).toUpperCase() : '?'}
              </Text>
            </View>
          )}
          <View style={styles.nameContainer}>
            <View style={styles.userRow}>
              <Text style={styles.authorName}>
                {isRetweet ? `@${retweetInfo?.username}` : tweet.author_name}
              </Text>
              {!isRetweet && tweet.verified_type &&
                (tweet.verified_type === 'blue' ||
                  tweet.verified_type === 'business' ||
                  tweet.verified_type === 'government') && (
                  <View style={[styles.verifiedBadge,
                  tweet.verified_type === 'blue' ? styles.verifiedBlue :
                    tweet.verified_type === 'business' ? styles.verifiedGold :
                      styles.verifiedGray]}>
                    <Text style={styles.verifiedText}>✓</Text>
                  </View>
                )}
            </View>
            <View style={styles.usernameRow}>
              {!isRetweet && (
                <Text style={styles.username}>@{tweet.username}</Text>
              )}
              {!(isFromRss || isFromWebz) && (
                <TouchableOpacity onPress={handleOpenPress}>
                  {isFromTv ? (
                    <FaTv style={{ color: '#9f1239', fontSize: 16, marginLeft: 4, marginRight: 4 }} />
                  ) : isFromFacebookGroup ? (
                    <FaFacebookF style={{ color: '#1877F2', fontSize: 16, marginLeft: 4, marginRight: 4 }} />
                  ) : isFromTiktok ? (
                    <FaTiktok style={{ color: 'var(--text-primary)', fontSize: 16, marginLeft: 4, marginRight: 4 }} />
                  ) : isFromInstagram ? (
                    <Image
                      source={InstagramIcon}
                      style={[styles.logoImage, styles.clickableIcon]}
                    />
                  ) : (
                    <Image
                      source={isFromTelegram ? TelegramIcon : TwitterIcon}
                      style={[styles.logoImage, styles.clickableIcon]}
                    />
                  )}
                </TouchableOpacity>
              )}
              {tweet.is_wide_monitoring && (
                <span title="Wide Monitoring" style={{
                  fontSize: 13,
                  marginLeft: 4,
                  opacity: 0.75,
                  cursor: 'default'
                }}>🌐</span>
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>

      {/* Tweet content */}
      <TouchableOpacity style={styles.contentContainer} onPress={handleOpenPress}>
        {renderFormattedText()}

        {/* TikTok embed */}
        {isFromTiktok && tiktokVideoId && (
          <View style={{ marginTop: 12, marginBottom: 8, borderRadius: 12, overflow: 'hidden', backgroundColor: '#111', borderWidth: 1, borderColor: 'rgba(255,0,80,0.2)' }}>
            <iframe
              src={`https://www.tiktok.com/embed/v2/${tiktokVideoId}`}
              style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
              allowFullScreen
              allow="encrypted-media"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
          </View>
        )}

        {/* TV clip video */}
        {isFromTv && tweet.video_url && (
          <View
            style={{ marginTop: 12, marginBottom: 8 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <video
              src={tweet.video_url}
              controls
              playsInline
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ width: '100%', aspectRatio: '16/9', borderRadius: 12, display: 'block', backgroundColor: '#000' }}
            />
          </View>
        )}

        {/* Images if any */}
        {!isFromTiktok && !isFromTv && tweet.tweet_images && tweet.tweet_images.length > 0 && (
          <View style={styles.imagesContainer}>
            {(() => {
              const isVideoUrl = (url) => /\.(mp4|mov|webm|ogg)(\?.*)?$/i.test(url);
              const videoUrls = tweet.tweet_images.filter(isVideoUrl);
              const imageUrls = tweet.tweet_images.filter(url => !isVideoUrl(url));
              const hasVideos = videoUrls.length > 0;

              // Build display items: pair each image with a video overlay if videos exist,
              // or show a dark placeholder for any unpaired videos.
              const displayItems = imageUrls.length > 0
                ? imageUrls.map((imageUrl, i) => ({ type: 'image', imageUrl, hasVideo: hasVideos && i === 0 }))
                : videoUrls.map(() => ({ type: 'video-only' }));

              const mediaCount = displayItems.length;

              return displayItems.map((item, index) => {
                let mediaStyle = styles.tweetImage;
                if (mediaCount === 1) mediaStyle = styles.singleImage;
                else if (mediaCount === 2) mediaStyle = styles.doubleImage;
                else if (mediaCount >= 3) mediaStyle = styles.multipleImage;

                const isVideoItem = item.type === 'video-only' || item.hasVideo;
                const wrapperSizeStyle = isVideoItem ? styles.videoWrapper
                  : mediaCount === 1 ? styles.singleImageWrapper
                  : mediaCount === 2 ? styles.doubleImageWrapper
                  : styles.multipleImageWrapper;

                if (item.type === 'video-only') {
                  return (
                    <TouchableOpacity
                      key={index}
                      style={[styles.imageWrapper, wrapperSizeStyle, { position: 'relative' }]}
                      onPress={() => window.open(tweet.url, '_blank')}
                    >
                      <div style={{
                        width: '100%', height: '100%', minHeight: 120,
                        background: 'rgba(0,0,0,0.6)', borderRadius: 16,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <FaPlay style={{ fontSize: 32, color: 'white', opacity: 0.9 }} />
                      </div>
                    </TouchableOpacity>
                  );
                }

                return (
                  <TouchableOpacity
                    key={index}
                    style={[styles.imageWrapper, wrapperSizeStyle, { position: 'relative' }]}
                    onPress={() => window.open(item.hasVideo ? tweet.url : item.imageUrl, '_blank')}
                  >
                    <ImageWithFallback
                      source={{ uri: item.imageUrl }}
                      style={{ ...mediaStyle, backgroundColor: 'rgba(26, 26, 32, 0.95)' }}
                      resizeMode="contain"
                    />
                    {item.hasVideo && (
                      <View style={styles.playOverlay}>
                        <FaPlay style={styles.playIcon} />
                      </View>
                    )}
                  </TouchableOpacity>
                );
              });
            })()}
          </View>
        )}
      </TouchableOpacity>

      {/* Metadata */}
      <View style={styles.metadataContainer}>
        <Text style={styles.metadata}>
          {sortBy === 'inserted_at' && tweet.inserted_at
            ? t('app.tweetCell.fetched', { time: formatLocalTime(tweet.inserted_at) })
            : formatLocalTime(tweet.localCreatedAt || tweet.created_at || 'Unknown')}
        </Text>
        <View style={styles.listBadge}>
          <Text style={styles.listBadgeText}>#{tweet.list_name || 'N/A'}</Text>
        </View>
      </View>

      {/* Engagement stats */}
      {(() => {
        const fmt = (n) => {
          if (!n && n !== 0) return null;
          if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
          if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
          return String(n);
        };
        const views    = fmt(tweet.view_count);
        const likes    = fmt(tweet.like_count || tweet.likes);
        const comments = fmt(tweet.comment_count || tweet.reply_count || tweet.comments);
        const reposts  = fmt(tweet.retweet_count);
        const shares   = fmt(tweet.share_count);
        if (!views && !likes && !comments && !reposts && !shares) return null;
        return (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginStart: 64,
            marginBottom: 12, flexWrap: 'wrap' }}>
            {comments && <Text style={{ color: 'var(--text-muted)', fontSize: 13 }}>💬 {comments}</Text>}
            {reposts  && <Text style={{ color: 'var(--text-muted)', fontSize: 13 }}>🔁 {reposts}</Text>}
            {likes    && <Text style={{ color: 'var(--text-muted)', fontSize: 13 }}>♡ {likes}</Text>}
            {shares   && <Text style={{ color: 'var(--text-muted)', fontSize: 13 }}>↗ {shares}</Text>}
            {views    && <Text style={{ color: 'var(--text-muted)', fontSize: 13 }}>👁 {views}</Text>}
          </View>
        );
      })()}

      {/* Action buttons */}
      {!hideActions && (
        <View style={styles.actionsContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.actionsScrollView}>
            <View style={styles.actionsRow}>
              {/* Primary action buttons */}
              <View style={styles.primaryActions}>
                {/* Only show Reply button for reply tweets */}
                {tweetType === "reply" && (
                  <TouchableOpacity style={styles.actionButton} onPress={handleReplyPress}>
                    <View style={styles.actionIconContainer}>
                      <FaReply style={styles.actionIcon} />
                    </View>
                  </TouchableOpacity>
                )}

                {/* Only show Quote button for quoted tweets */}
                {tweetType === "quote" && (
                  <TouchableOpacity style={styles.actionButton} onPress={handleQuotePress}>
                    <View style={styles.actionIconContainer}>
                      <FaQuoteRight style={styles.actionIcon} />
                    </View>
                  </TouchableOpacity>
                )}

                {/* Info button */}
                <TouchableOpacity style={styles.actionButton} onPress={handleInfoPress}>
                  <View style={styles.actionIconContainer}>
                    <FaInfoCircle style={styles.actionIcon} />
                  </View>
                </TouchableOpacity>

                {/* Open button */}
                <TouchableOpacity style={styles.actionButton} onPress={handleOpenPress}>
                  <View style={styles.actionIconContainer}>
                    <FaExternalLinkAlt style={styles.actionIcon} />
                  </View>
                </TouchableOpacity>

                {/* Share button */}
                <TouchableOpacity style={styles.actionButton} onPress={handleSharePress}>
                  <View style={styles.actionIconContainer}>
                    <FaShareAlt style={styles.actionIcon} />
                  </View>
                </TouchableOpacity>

                {/* Draft Reaction button */}
                <TouchableOpacity
                  style={[styles.actionButton, styles.reactionButton]}
                  onPress={handleDraftReaction}
                >
                  <View style={[styles.actionIconContainer, styles.reactionIconContainer]}>
                    <FaMagic style={{ fontSize: 15, color: '#818cf8' }} />
                  </View>
                </TouchableOpacity>

                {/* Remove message button */}
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleRemove}
                >
                  <View style={[styles.actionIconContainer, styles.removeIconContainer]}>
                    <FaTrash style={{ fontSize: 13, color: '#ff453a', opacity: 0.7 }} />
                  </View>
                </TouchableOpacity>

                {/* Track engagement button */}
                <TouchableOpacity style={styles.actionButton} onPress={handleTrackPost} disabled={isTracking || isTracked}>
                  <View style={[styles.actionIconContainer, isTracked ? styles.trackedIconContainer : styles.trackIconContainer]}>
                    {isTracking ? (
                      <ActivityIndicator size="small" color="#0a84ff" />
                    ) : (
                      <FaBinoculars style={{ fontSize: 13, color: isTracked ? '#30d158' : '#0a84ff' }} />
                    )}
                  </View>
                </TouchableOpacity>
              </View>

              {/* Translate button */}
              <TouchableOpacity
                style={[
                  styles.translateButton,
                  isTranslated && styles.translateButtonActive,
                  isTranslating && styles.translateButtonLoading
                ]}
                onPress={handleTranslatePress}
                disabled={isTranslating}
              >
                {isTranslating ? (
                  <ActivityIndicator size="small" color="#0f9bff" style={{ marginRight: 4 }} />
                ) : isTranslated ? (
                  <FaUndo style={styles.translateIcon} />
                ) : (
                  <FaLanguage style={styles.translateIcon} />
                )}
                <Text style={styles.translateButtonText}>
                  {isTranslating ? t('app.tweetCell.translating') : isTranslated ? t('app.tweetCell.showOriginal') : t('app.tweetCell.translate')}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      )}

      {/* Reason Modal */}
      {renderReasonModal()}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal tweet={tweet} onClose={() => setShowShareModal(false)} />
      )}
    </View>
  );
};

// Helper component for images with loading state and fallback
const ImageWithFallback = ({ source, style, resizeMode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <View style={[style, { position: 'relative' }]}>
      {loading && (
        <View style={styles.imageLoadingContainer}>
          <ActivityIndicator size="small" color="#0066cc" />
        </View>
      )}

      {error && (
        <View style={styles.imageFallbackContainer}>
          <Text style={styles.imageFallbackText}>🖼️</Text>
        </View>
      )}

      <Image
        source={source}
        style={[style, { display: error ? 'none' : 'flex' }]}
        resizeMode={resizeMode}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    borderRadius: 24,
    marginHorizontal: 12,
    marginVertical: 12,
    borderWidth: 1,
    backgroundColor: 'var(--bg-card)',
    borderColor: 'var(--border)',
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '&:hover': {
      transform: 'translateY(-4px) scale(1.01)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
    },
  },
  retweetLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  retweetLabelIcon: {
    fontSize: 14,
    marginRight: 6,
    color: 'var(--text-secondary)',
  },
  retweetLabel: {
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: 'var(--accent-primary)',
    justifyContent: 'center',
    alignItems: 'center',
    marginEnd: 14,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  avatarText: {
    color: 'white',
    fontSize: 20,
    fontWeight: 'bold',
  },
  profileImage: {
    width: 50,
    height: 50,
    borderRadius: 16,
    marginEnd: 14,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  },
  nameContainer: {
    flex: 1,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  usernameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  authorName: {
    color: 'var(--text-primary)',
    fontWeight: '700',
    fontSize: 16,
    marginRight: 6,
    letterSpacing: '-0.01em',
  },
  verifiedBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginLeft: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  verifiedBlue: {
    backgroundColor: '#1DA1F2',
  },
  verifiedGold: {
    backgroundColor: '#FFD700',
  },
  verifiedGray: {
    backgroundColor: '#888888',
  },
  verifiedText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
  username: {
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  logoImage: {
    width: 16,
    height: 16,
    marginLeft: 'auto',
    opacity: 0.6,
  },
  contentContainer: {
    marginStart: 64,
  },
  tweetContent: {
    color: 'var(--text-primary)', // Slight off-white for better readability
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 16,
    userSelect: 'text',
    cursor: 'text',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
  },
  boldText: {
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  linkText: {
    color: 'var(--accent-primary)',
    textDecorationLine: 'none',
    fontWeight: '500',
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
    marginBottom: 20,
    width: '100%',
    justifyContent: 'flex-start',
    gap: 8,
  },
  imageWrapper: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'var(--bg-card)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
  },
  videoWrapper: {
    width: '45%',
    maxWidth: 200,
    aspectRatio: 9 / 16,
  },
  singleImageWrapper: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: 16 / 9,
  },
  doubleImageWrapper: {
    width: '48%',
    aspectRatio: 1,
  },
  multipleImageWrapper: {
    width: '31%',
    aspectRatio: 1,
  },
  tweetImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  singleImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  doubleImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  multipleImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  metadataContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginStart: 64,
    marginTop: 4,
    marginBottom: 16,
  },
  metadata: {
    fontSize: 13,
    color: 'var(--text-muted)',
    marginRight: 12,
    fontWeight: '500',
  },
  listBadge: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.2)',
  },
  listBadgeText: {
    color: 'var(--accent-primary)',
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  actionsContainer: {
    marginTop: 8,
    marginStart: 64,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.05)',
    paddingTop: 16,
  },
  actionsScrollView: {
    marginBottom: 0,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    marginRight: 0,
  },
  actionIconContainer: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
    minWidth: 40,
    minHeight: 40,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transform: 'translateY(-2px)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  actionIcon: {
    fontSize: 15,
    color: 'var(--text-secondary)',
  },
  reactionButton: {
    // Distinctive styling for AI reaction button
  },
  removeIconContainer: {
    backgroundColor: 'rgba(255, 69, 58, 0.08)',
    borderColor: 'rgba(255, 69, 58, 0.15)',
  },
  trackIconContainer: {
    backgroundColor: 'rgba(10, 132, 255, 0.08)',
    borderColor: 'rgba(10, 132, 255, 0.15)',
  },
  trackedIconContainer: {
    backgroundColor: 'rgba(48, 209, 88, 0.15)',
    borderColor: 'rgba(48, 209, 88, 0.3)',
  },
  reactionIconContainer: {
    backgroundColor: 'rgba(99, 102, 241, 0.15)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(99, 102, 241, 0.25)',
      borderColor: 'rgba(99, 102, 241, 0.5)',
    },
  },
  reactionIcon: {
    color: '#818cf8',
  },
  translateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.2)',
    marginLeft: 12,
    gap: 6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      transform: 'translateY(-1px)',
      boxShadow: '0 0 15px rgba(59, 130, 246, 0.2)',
    },
  },
  translateIcon: {
    fontSize: 16,
    color: 'var(--accent-primary)',
    marginRight: 8,
  },
  translateButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: 'var(--accent-primary)',
  },
  imageLoadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  imageFallbackContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-card)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  imageFallbackText: {
    fontSize: 24,
    color: 'var(--text-muted)',
  },
  clickableIcon: {
    cursor: 'pointer',
    opacity: 0.8,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1,
    },
  },
  translationIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  translationIcon: {
    fontSize: 12,
    color: 'var(--accent-primary)',
    marginRight: 4,
  },
  translationText: {
    fontSize: 12,
    fontWeight: '500',
    color: 'var(--accent-primary)',
    opacity: 0.9,
  },
  highlightedText: {
    backgroundColor: 'rgba(250, 204, 21, 0.35)',
    borderRadius: 2,
    color: 'var(--text-primary)',
    fontWeight: '600',
  },
  translateButtonActive: {
    backgroundColor: 'rgba(59, 130, 246, 0.25)',
    borderColor: 'rgba(59, 130, 246, 0.4)',
  },
  translateButtonLoading: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.2)',
    opacity: 0.7,
  },
  playOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.15)',
  },
  playIcon: {
    fontSize: 32,
    color: 'white',
    filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
  },
});

export default TweetCell; 
